import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Dialog, InputAdornment, Stack, TextField, Typography, useTheme } from '@mui/material';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nCommonNs } from '@/i18n/dictionaries/interfaces.ts';
import SearchIcon from '@mui/icons-material/Search';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HistoryIcon from '@mui/icons-material/History';
import ViewListIcon from '@mui/icons-material/ViewList';
import SchoolIcon from '@mui/icons-material/School';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import TranslateIcon from '@mui/icons-material/Translate';
import PersonIcon from '@mui/icons-material/Person';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BusinessIcon from '@mui/icons-material/Business';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import BarChartIcon from '@mui/icons-material/BarChart';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import LogoutIcon from '@mui/icons-material/Logout';
import EventNoteIcon from '@mui/icons-material/EventNote';
import Fuse from 'fuse.js';
import { generatePath } from 'react-router';

import AppRoutes from 'app/utils/routes.ts';
import * as ScenarioSelectors from 'app/selectors/scenarioSelectors.ts';
import * as UserSelectors from 'app/selectors/userSelectors.ts';
import { GuiStateActions } from 'app/actions/guiState/guiStateAction.ts';
import * as ScenarioActions from 'app/actions/scenarios/scenarioAction.ts';
import {
  isUserAnyAdminSelector,
  isUserOwnerSelector,
  isUserSuperAdminSelector,
  shouldUseLegacyVadSelector
} from 'app/selectors/userSelectors.ts';
import { sideBarStateSelector } from 'app/selectors/guiStateSelectors.ts';
import type { ScenarioDto } from '@/api-gen/api.schemas';
import { useLogout } from 'app/hooks/useLogout.ts';

type CommandKind = 'internal' | 'external' | 'action';

type CommandOption = {
  id: string;
  label: string;
  group: string;
  type: CommandKind;
  path?: string;
  description?: string;
  meta?: string;
  keywords?: Array<string>;
  icon?: React.ReactNode;
  action?: () => void;
};

type GroupHeader = {
  isHeader: true;
  group: string;
};

type ListItem = CommandOption | GroupHeader;

const isGroupHeader = (item: ListItem): item is GroupHeader => 'isHeader' in item && item.isHeader;

type VirtuosoContext = {
  activeCommandIndex: number;
  commandIndexMap: Map<number, number>; // listIndex -> commandIndex
  handleSelect: (option: CommandOption) => void;
  setActiveCommandIndex: React.Dispatch<React.SetStateAction<number>>;
  primaryColor: string;
};

const LISTBOX_HEIGHT = 420;

// Define List component outside to prevent remounting on every render
const VirtuosoList = React.forwardRef<HTMLDivElement, React.ComponentPropsWithRef<'div'>>((props, ref) => (
  <Box {...props} ref={ref} component="div" role="listbox" id="command-palette-listbox" />
));
VirtuosoList.displayName = 'VirtuosoList';

const VirtuosoHeader = () => <Box sx={{ height: 8 }} aria-hidden />;

const CommandPalette: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [activeCommandIndex, setActiveCommandIndex] = useState(0);
  const [listReady, setListReady] = useState(false);
  const deferredInputValue = useDeferredValue(inputValue);
  const dispatch = useDispatch();
  const history = useHistory();
  const logout = useLogout();
  const [t] = useTranslation([I18nNamespace.Common]);
  const theme = useTheme();
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const currentUser = useSelector(UserSelectors.CurrentUserSelector);
  const scenarios = useSelector(ScenarioSelectors.scenariosOrvalSelector);
  const scenariosLoading = useSelector(ScenarioSelectors.scenariosLoading);
  const useLegacyVad = useSelector(shouldUseLegacyVadSelector);
  const isUserAnyAdmin = useSelector(isUserAnyAdminSelector);
  const isUserOwner = useSelector(isUserOwnerSelector);
  const isUserSuperAdmin = useSelector(isUserSuperAdminSelector);
  const sideBarOpenState = useSelector(sideBarStateSelector);
  const hasSkllsAccess = !!(currentUser && currentUser.hasSklls === true);
  const hasInterpreterAccess = !!(currentUser && currentUser.hasInterpreter === true);
  const hasExercisesAccess = !!(currentUser && currentUser.hasExercises === true);
  const previousSideBarStateRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (open && scenarios.length === 0 && !scenariosLoading) {
      dispatch(ScenarioActions.getScenarios());
    }
  }, [dispatch, open, scenarios.length, scenariosLoading]);

  const baseCommands: Array<CommandOption> = useMemo(() => {
    const commands: Array<CommandOption> = [];

    if (hasSkllsAccess) {
      commands.push(
        {
          id: 'nav-dashboard',
          label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.goToDashboard)),
          group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.navigation)),
          type: 'internal',
          path: AppRoutes.Dashboard,
          icon: <DashboardIcon />
        },
        {
          id: 'nav-scenarios',
          label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.browseScenarios)),
          group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.navigation)),
          type: 'internal',
          path: AppRoutes.Scenarios,
          icon: <ViewListIcon />
        },
        {
          id: 'nav-conversations',
          label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.conversationHistory)),
          group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.navigation)),
          type: 'internal',
          path: AppRoutes.Conversations,
          icon: <HistoryIcon />
        },
        {
          id: 'nav-my-courses',
          label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.myCourses)),
          group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.navigation)),
          type: 'internal',
          path: AppRoutes.MyCourses,
          icon: <SchoolIcon />
        },
        {
          id: 'nav-user-guides',
          label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.userGuides)),
          group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.help)),
          type: 'internal',
          path: AppRoutes.UserGuides,
          icon: <HelpOutlineIcon />
        }
      );
    }

    if (hasSkllsAccess && isUserAnyAdmin) {
      commands.push(
        {
          id: 'nav-user-management',
          label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.userManagement)),
          group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.admin)),
          type: 'internal',
          path: AppRoutes.UserManagement,
          icon: <PersonIcon />
        },
        {
          id: 'nav-groups',
          label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.groupManagement)),
          group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.admin)),
          type: 'internal',
          path: AppRoutes.GroupManagement,
          icon: <GroupWorkIcon />
        },
        {
          id: 'nav-courses',
          label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.courseManagement)),
          group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.admin)),
          type: 'internal',
          path: AppRoutes.CourseManagement,
          icon: <SchoolIcon />
        },
        {
          id: 'nav-global-assets',
          label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.globalAssets)),
          group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.admin)),
          type: 'internal',
          path: AppRoutes.GlobalAssetsLibrary,
          icon: <LibraryAddCheckIcon />
        },
        {
          id: 'nav-call-management',
          label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.callManagement)),
          group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.admin)),
          type: 'internal',
          path: AppRoutes.CallManagement,
          icon: <SupportAgentIcon />
        },
        {
          id: 'nav-evaluation-protocols',
          label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.evaluationProtocols)),
          group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.admin)),
          type: 'internal',
          path: AppRoutes.EvaluationProtocolManager,
          icon: <AssessmentIcon />
        },
        {
          id: 'nav-personas',
          label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.personas)),
          group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.admin)),
          type: 'internal',
          path: AppRoutes.PersonasManager,
          icon: <PersonIcon />
        }
      );
    }

    if (hasSkllsAccess && (hasInterpreterAccess || isUserSuperAdmin || isUserOwner)) {
      commands.push({
        id: 'nav-translator',
        label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.translator)),
        group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.navigation)),
        type: 'internal',
        path: AppRoutes.Translator,
        icon: <TranslateIcon />
      });
    }

    if (hasSkllsAccess && isUserSuperAdmin) {
      commands.push(
        {
          id: 'nav-companies',
          label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.companies)),
          group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.admin)),
          type: 'internal',
          path: AppRoutes.CompaniesManagement,
          icon: <BusinessIcon />
        },
        {
          id: 'nav-recent-jobs',
          label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.recentJobs)),
          group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.admin)),
          type: 'internal',
          path: AppRoutes.RecentJobs,
          icon: <ErrorOutlineIcon />
        },
        {
          id: 'nav-exercises',
          label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.exercises)),
          group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.admin)),
          type: 'internal',
          path: AppRoutes.ExercisesManager,
          icon: <EventNoteIcon />
        },
        {
          id: 'nav-statistics',
          label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.statisticsHistorical)),
          group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.insights)),
          type: 'internal',
          path: AppRoutes.Statistics,
          icon: <BarChartIcon />
        },
        {
          id: 'nav-usage-stats',
          label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.usageStatistics)),
          group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.insights)),
          type: 'internal',
          path: AppRoutes.UsageStatistics,
          icon: <AnalyticsIcon />
        }
      );
    }

    commands.push({
      id: 'action-logout',
      label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.labels.signOut)),
      group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.account)),
      type: 'action',
      action: logout,
      icon: <LogoutIcon />
    });

    return commands;
  }, [hasInterpreterAccess, hasSkllsAccess, hasExercisesAccess, isUserAnyAdmin, isUserOwner, isUserSuperAdmin, logout, t]);

  const scenarioCommands: Array<CommandOption> = useMemo(() => {
    return scenarios
      .filter((scenario: ScenarioDto | null): scenario is ScenarioDto => !!scenario && !!scenario.id)
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      .flatMap((scenario) => {
        const scenarioName = scenario.name ?? t(nameof.full<I18nCommonNs>((n) => n.commandPalette.scenarios.untitled));
        const editorPath = generatePath(AppRoutes.SceneWithId, { id: scenario.id });
        const playerPath = generatePath(AppRoutes.ScenarioPlayer, { id: scenario.id });

        return [
          {
            id: `scenario-start-${scenario.id}`,
            label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.scenarios.startScenario), { scenarioName }),
            group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.scenarios)),
            type: 'internal',
            path: useLegacyVad ? editorPath : playerPath,
            icon: <PlayCircleOutlineIcon />,
            description: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.scenarios.startDescription)),
            meta: `#${scenario.id}`,
            keywords: [scenarioName, String(scenario.id)]
          },
          {
            id: `scenario-editor-${scenario.id}`,
            label: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.scenarios.scenarioEditor), { scenarioName }),
            group: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.groups.scenarioEditor)),
            type: 'internal',
            path: editorPath,
            icon: <EditIcon />,
            description: t(nameof.full<I18nCommonNs>((n) => n.commandPalette.scenarios.editorDescription)),
            meta: `#${scenario.id}`,
            keywords: [scenarioName, String(scenario.id)]
          }
        ];
      });
  }, [scenarios, useLegacyVad, t]);

  const allCommands = useMemo(() => [...baseCommands, ...scenarioCommands], [baseCommands, scenarioCommands]);

  const fuse = useMemo(
    () =>
      new Fuse(allCommands, {
        keys: ['label', 'description', 'meta', 'keywords'],
        threshold: 0.35
      }),
    [allCommands]
  );

  const filteredCommands = useMemo(() => {
    const query = deferredInputValue.trim();
    if (!query) {
      return allCommands;
    }

    return fuse.search(query).map((result) => result.item);
  }, [allCommands, fuse, deferredInputValue]);

  // Group commands and interleave headers
  const { listItems, commandIndexMap, commandToListIndex } = useMemo(() => {
    const items: ListItem[] = [];
    const indexMap = new Map<number, number>(); // listIndex -> commandIndex
    const reverseMap = new Map<number, number>(); // commandIndex -> listIndex
    let commandIdx = 0;
    let currentGroup = '';

    for (const command of filteredCommands) {
      if (command.group !== currentGroup) {
        currentGroup = command.group;
        items.push({ isHeader: true, group: currentGroup });
      }
      const listIdx = items.length;
      items.push(command);
      indexMap.set(listIdx, commandIdx);
      reverseMap.set(commandIdx, listIdx);
      commandIdx++;
    }

    return { listItems: items, commandIndexMap: indexMap, commandToListIndex: reverseMap };
  }, [filteredCommands]);

  // Use ref to access current data in event listener without re-binding
  const filteredCommandsRef = useRef(filteredCommands);
  filteredCommandsRef.current = filteredCommands;
  const commandToListIndexRef = useRef(commandToListIndex);
  commandToListIndexRef.current = commandToListIndex;

  const totalItemCount = listItems.length;
  const totalCommandCount = filteredCommands.length;

  const scrollToItem = useCallback(
    (index: number, align: 'start' | 'center' | 'end' = 'center') => {
      if (!virtuosoRef.current) return;
      virtuosoRef.current.scrollToIndex({ index, behavior: 'auto', align });
    },
    []
  );

  // Reset active command index when filtered commands change
  useEffect(() => {
    setActiveCommandIndex(0);
    if (open && virtuosoRef.current) {
      virtuosoRef.current.scrollTo({ top: 0 });
    }
  }, [filteredCommands, open]);

  useEffect(() => {
    if (!open) {
      setListReady(false);
    }
  }, [open]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setInputValue('');
    setActiveCommandIndex(0);
  }, []);

  const handleSelect = useCallback(
    (option: CommandOption) => {
      handleClose();

      if (option.type === 'action' && option.action) {
        option.action();
        return;
      }

      if (option.path) {
        if (option.type === 'external') {
          window.location.assign(option.path);
        } else {
          history.push(option.path);
        }
      }
    },
    [handleClose, history]
  );

  // Keyboard navigation - use refs to avoid rebinding on every activeCommandIndex change
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Cmd/Ctrl+K to toggle
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        if (open) {
          handleClose();
        } else {
          setOpen(true);
          setInputValue('');
          setActiveCommandIndex(0);
        }
        return;
      }

      if (!open) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
        return;
      }

      const commandCount = filteredCommandsRef.current.length;
      if (commandCount === 0) return; // Guard against empty list

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setActiveCommandIndex((prev) => {
            const next = prev + 1 >= commandCount ? 0 : prev + 1;
            const listIdx = commandToListIndexRef.current.get(next);
            if (listIdx !== undefined) scrollToItem(listIdx);
            return next;
          });
          break;
        case 'ArrowUp':
          event.preventDefault();
          setActiveCommandIndex((prev) => {
            const next = prev - 1 < 0 ? commandCount - 1 : prev - 1;
            const listIdx = commandToListIndexRef.current.get(next);
            if (listIdx !== undefined) scrollToItem(listIdx);
            return next;
          });
          break;
        case 'Enter':
          event.preventDefault();
          setActiveCommandIndex((currentIndex) => {
            const item = filteredCommandsRef.current[currentIndex];
            if (item) handleSelect(item);
            return currentIndex;
          });
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [open, handleClose, handleSelect]);

  useEffect(() => {
    const handleOpenCommandPalette = () => {
      setOpen(true);
      setInputValue('');
      setActiveCommandIndex(0);
    };

    window.addEventListener('open-command-palette', handleOpenCommandPalette);
    return () => window.removeEventListener('open-command-palette', handleOpenCommandPalette);
  }, []);

  useEffect(() => {
    // Keep the sidebar open while the palette is shown, then restore the user's prior preference.
    if (open) {
      if (previousSideBarStateRef.current === null) {
        previousSideBarStateRef.current = sideBarOpenState;
      }

      if (!sideBarOpenState) {
        dispatch(GuiStateActions.sideMenuOpen(true));
      }

      return;
    }

    if (previousSideBarStateRef.current !== null && sideBarOpenState !== previousSideBarStateRef.current) {
      dispatch(GuiStateActions.sideMenuOpen(previousSideBarStateRef.current));
    }

    previousSideBarStateRef.current = null;
  }, [dispatch, open, sideBarOpenState]);

  // Focus the input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Use context to pass activeCommandIndex without having it as a dependency
  const renderItem = useCallback(
    (index: number, _data: unknown, context: VirtuosoContext) => {
      const item = listItems[index];
      if (!item) return null;

      // Render group header
      if (isGroupHeader(item)) {
        return (
          <Box
            component="div"
            role="presentation"
            sx={{
              px: 3.5,
              pt: 2,
              pb: 0.5
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              {item.group}
            </Typography>
          </Box>
        );
      }

      // Render command option
      const commandIndex = context.commandIndexMap.get(index);
      const isActive = commandIndex === context.activeCommandIndex;

      return (
        <Box
          component="div"
          role="option"
          id={`command-option-${item.id}`}
          aria-selected={isActive}
          onClick={() => context.handleSelect(item)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            mx: 1.5,
            px: 2,
            py: 1,
            gap: 2,
            borderRadius: '6rem',
            cursor: 'pointer',
            transition: 'background-color 150ms ease',
            '&:hover': {
              backgroundColor: 'primary.100'
            },
            ...(isActive && {
              backgroundColor: 'primary.100',
              outline: `2px solid ${context.primaryColor}`,
              outlineOffset: -2
            })
          }}
        >
          <Box sx={{ color: 'text.secondary', display: 'flex', flexShrink: 0 }}>{item.icon}</Box>
          <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" noWrap sx={{ fontWeight: 600 }}>
              {item.label}
            </Typography>
            {item.description && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {item.description}
              </Typography>
            )}
          </Stack>
          {item.meta && (
            <Chip
              size="small"
              label={item.meta}
              variant="outlined"
              sx={{ ml: 1, bgcolor: 'action.hover', borderColor: 'divider', flexShrink: 0 }}
            />
          )}
        </Box>
      );
    },
    [listItems]
  );

  const activeOption = filteredCommands[activeCommandIndex];
  const activeDescendantId = activeOption ? `command-option-${activeOption.id}` : undefined;

  // Context for Virtuoso - passes activeCommandIndex without adding it as renderItem dependency
  const virtuosoContext: VirtuosoContext = useMemo(
    () => ({
      activeCommandIndex,
      commandIndexMap,
      handleSelect,
      setActiveCommandIndex,
      primaryColor: theme.palette.primary.main
    }),
    [activeCommandIndex, commandIndexMap, handleSelect, theme.palette.primary.main]
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown
      fullWidth
      maxWidth="sm"
      TransitionProps={{
        onEntered: () => setListReady(true)
      }}
      PaperProps={{
        sx: {
          position: 'fixed',
          top: theme.mixins.denseToolbar.minHeight,
          borderRadius: 2,
          boxShadow: 16,
          border: `1px solid ${theme.palette.grey[200]}`,
          overflow: 'hidden'
        }
      }}
    >
      <Box role="combobox" aria-expanded={open} aria-haspopup="listbox" aria-owns="command-palette-listbox">
        <TextField
          inputRef={inputRef}
          placeholder={t(nameof.full<I18nCommonNs>((n) => n.commandPalette.placeholder))}
          variant="standard"
          fullWidth
          autoFocus
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          inputProps={{
            'aria-label': t(nameof.full<I18nCommonNs>((n) => n.commandPalette.searchAriaLabel)),
            'aria-autocomplete': 'list',
            'aria-controls': 'command-palette-listbox',
            'aria-activedescendant': activeDescendantId
          }}
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start" sx={{ pl: 2 }}>
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            sx: {
              p: 2,
              height: 56,
              fontSize: '1rem',
              borderBottom: `1px solid ${theme.palette.grey[200]}`
            }
          }}
        />

        {listReady ? (
          totalItemCount > 0 ? (
            <Virtuoso
              ref={virtuosoRef}
              style={{ height: LISTBOX_HEIGHT }}
              totalCount={totalItemCount}
              itemContent={(index, _data, context) => renderItem(index, _data, context as VirtuosoContext)}
              context={virtuosoContext}
              components={{ List: VirtuosoList, Header: VirtuosoHeader }}
            />
          ) : (
            <Box role="status" aria-live="polite" sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                {inputValue
                  ? t(nameof.full<I18nCommonNs>((n) => n.commandPalette.noMatches))
                  : t(nameof.full<I18nCommonNs>((n) => n.commandPalette.startTyping))}
              </Typography>
              {inputValue && (
                <Typography color="text.disabled" variant="caption">
                  {t(nameof.full<I18nCommonNs>((n) => n.commandPalette.tryDifferentSearch))}
                </Typography>
              )}
            </Box>
          )
        ) : (
          <Box role="presentation" aria-hidden sx={{ height: LISTBOX_HEIGHT }} />
        )}
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
